/**
 * Reading the old system's staff spreadsheet.
 *
 * Old HR and payroll systems export with their own headings, in English or
 * Arabic, their own date formats (including Hijri), Arabic-Indic digits,
 * "SAR 5,000", nationality written as a word. This file:
 *
 *   1. finds the heading row and matches each column to a field by its
 *      heading (a person can change any match);
 *   2. turns each cell into the platform's value, or says why it cannot;
 *   3. never guesses a value it cannot read: the cell is reported, and the
 *      row is either refused (a required field) or imported without it.
 *
 * The platform checks every row again on import — this is so the person sees
 * the problems before pressing the button, not instead of the platform.
 */
import { TRADES } from "./hr";

export type FieldKind =
  | "text"
  | "date"
  | "number"
  | "nationality"
  | "employment"
  | "contract"
  | "status"
  | "gender"
  | "marital"
  | "trade"
  | "phone"
  | "email"
  | "methods";

export interface ImportField {
  key: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  pay?: boolean;
  /** headings this field is known by, lower-case, English and Arabic */
  synonyms: string[];
}

export const IMPORT_FIELDS: ImportField[] = [
  {
    key: "employee_no",
    label: "Employee number",
    kind: "text",
    required: true,
    synonyms: [
      "employee no",
      "employee number",
      "emp no",
      "emp. no",
      "emp id",
      "employee id",
      "staff no",
      "staff id",
      "staff number",
      "badge no",
      "code",
      "employee code",
      "رقم الموظف",
      "الرقم الوظيفي",
      "رقم وظيفي",
      "كود الموظف",
    ],
  },
  {
    key: "name",
    label: "Name",
    kind: "text",
    required: true,
    synonyms: [
      "name",
      "full name",
      "employee name",
      "staff name",
      "name (english)",
      "english name",
      "الاسم",
      "اسم الموظف",
      "الاسم الكامل",
    ],
  },
  {
    key: "nationality",
    label: "Nationality",
    kind: "nationality",
    required: true,
    synonyms: ["nationality", "country", "citizenship", "الجنسية"],
  },
  {
    key: "hired_on",
    label: "Hired on",
    kind: "date",
    required: true,
    synonyms: [
      "hired on",
      "hire date",
      "date of hire",
      "joining date",
      "date of joining",
      "doj",
      "join date",
      "start date",
      "employment date",
      "تاريخ التعيين",
      "تاريخ المباشرة",
      "تاريخ الالتحاق",
    ],
  },
  {
    key: "department",
    label: "Department",
    kind: "text",
    synonyms: ["department", "dept", "division", "section", "الإدارة", "القسم", "الادارة"],
  },
  {
    key: "job_title",
    label: "Job title",
    kind: "text",
    synonyms: [
      "job title",
      "title",
      "position",
      "designation",
      "job",
      "occupation",
      "المسمى الوظيفي",
      "الوظيفة",
      "المهنة",
    ],
  },
  {
    key: "trade",
    label: "Trade",
    kind: "trade",
    synonyms: ["trade", "craft", "discipline", "skill", "التخصص", "الحرفة"],
  },
  { key: "grade", label: "Grade", kind: "text", synonyms: ["grade", "level", "band", "الدرجة", "المرتبة"] },
  {
    key: "employment",
    label: "Employment",
    kind: "employment",
    synonyms: [
      "employment",
      "employment type",
      "employee type",
      "staff type",
      "direct/outsourced",
      "نوع التوظيف",
      "نوع العمالة",
    ],
  },
  { key: "contract_type", label: "Contract", kind: "contract", synonyms: ["contract", "contract type", "نوع العقد"] },
  {
    key: "contract_end",
    label: "Contract ends",
    kind: "date",
    synonyms: ["contract end", "contract end date", "contract expiry", "نهاية العقد", "تاريخ انتهاء العقد"],
  },
  {
    key: "status",
    label: "Status",
    kind: "status",
    synonyms: ["status", "employee status", "employment status", "الحالة", "حالة الموظف"],
  },
  {
    key: "left_on",
    label: "Left on",
    kind: "date",
    synonyms: [
      "left on",
      "termination date",
      "end of service date",
      "exit date",
      "last working day",
      "resignation date",
      "تاريخ ترك العمل",
      "تاريخ انتهاء الخدمة",
    ],
  },
  {
    key: "basic",
    label: "Basic",
    kind: "number",
    pay: true,
    synonyms: ["basic", "basic salary", "basic pay", "الراتب الأساسي", "الأساسي", "الراتب الاساسي"],
  },
  {
    key: "housing",
    label: "Housing",
    kind: "number",
    pay: true,
    synonyms: ["housing", "housing allowance", "house allowance", "بدل السكن", "السكن"],
  },
  {
    key: "transport",
    label: "Transport",
    kind: "number",
    pay: true,
    synonyms: [
      "transport",
      "transportation",
      "transport allowance",
      "transportation allowance",
      "بدل النقل",
      "بدل المواصلات",
      "النقل",
    ],
  },
  {
    key: "other",
    label: "Other fixed allowances",
    kind: "number",
    pay: true,
    synonyms: ["other", "other allowance", "other allowances", "بدلات أخرى", "بدلات اخرى"],
  },
  {
    key: "outsourced_monthly",
    label: "Contract charge a month",
    kind: "number",
    pay: true,
    synonyms: ["contract charge", "monthly charge", "contractor rate", "rate per month", "تكلفة المقاول"],
  },
  {
    key: "medical_annual",
    label: "Medical premium a year",
    kind: "number",
    pay: true,
    synonyms: ["medical", "medical premium", "insurance premium", "التأمين الطبي"],
  },
  {
    key: "weekly_hours",
    label: "Hours a week",
    kind: "number",
    synonyms: ["weekly hours", "hours per week", "hours a week", "ساعات العمل الأسبوعية"],
  },
  {
    key: "cost_centre",
    label: "Cost center",
    kind: "text",
    synonyms: ["cost centre", "cost center", "cc", "مركز التكلفة"],
  },
  {
    key: "date_of_birth",
    label: "Date of birth",
    kind: "date",
    synonyms: ["date of birth", "dob", "birth date", "birthdate", "تاريخ الميلاد"],
  },
  { key: "gender", label: "Gender", kind: "gender", synonyms: ["gender", "sex", "الجنس"] },
  {
    key: "marital_status",
    label: "Marital status",
    kind: "marital",
    synonyms: ["marital status", "marital", "الحالة الاجتماعية"],
  },
  {
    key: "id_number",
    label: "ID / iqama number",
    kind: "text",
    synonyms: [
      "id number",
      "id no",
      "iqama",
      "iqama no",
      "iqama number",
      "national id",
      "national id no",
      "civil id",
      "رقم الهوية",
      "رقم الإقامة",
      "رقم الاقامة",
      "الهوية",
      "الإقامة",
    ],
  },
  {
    key: "id_expiry",
    label: "ID / iqama expires",
    kind: "date",
    synonyms: [
      "id expiry",
      "iqama expiry",
      "iqama expiry date",
      "id expiry date",
      "انتهاء الإقامة",
      "انتهاء الهوية",
      "تاريخ انتهاء الإقامة",
    ],
  },
  {
    key: "passport_no",
    label: "Passport number",
    kind: "text",
    synonyms: ["passport", "passport no", "passport number", "رقم الجواز", "رقم جواز السفر"],
  },
  {
    key: "passport_expiry",
    label: "Passport expires",
    kind: "date",
    synonyms: ["passport expiry", "passport expiry date", "انتهاء الجواز"],
  },
  {
    key: "phone",
    label: "Mobile",
    kind: "phone",
    synonyms: [
      "mobile",
      "mobile no",
      "mobile number",
      "phone",
      "phone no",
      "phone number",
      "contact number",
      "الجوال",
      "رقم الجوال",
      "الهاتف",
    ],
  },
  {
    key: "email",
    label: "Email",
    kind: "email",
    synonyms: ["email", "e-mail", "email address", "البريد الإلكتروني", "البريد"],
  },
  { key: "address", label: "Address", kind: "text", synonyms: ["address", "home address", "العنوان"] },
  {
    key: "ec_name",
    label: "Emergency contact",
    kind: "text",
    synonyms: ["emergency contact", "emergency contact name", "جهة الاتصال في الطوارئ"],
  },
  {
    key: "ec_phone",
    label: "Emergency contact mobile",
    kind: "phone",
    synonyms: ["emergency phone", "emergency contact number", "emergency mobile", "رقم الطوارئ"],
  },
  {
    key: "login_email",
    label: "Sign-in email (links the login)",
    kind: "email",
    synonyms: ["login email", "sign-in email", "username", "user email", "بريد الدخول"],
  },
  {
    key: "leave_balance",
    label: "Leave balance (days)",
    kind: "number",
    synonyms: [
      "leave balance",
      "vacation balance",
      "annual leave balance",
      "leave days",
      "balance",
      "رصيد الإجازات",
      "رصيد الاجازات",
      "رصيد الإجازة",
    ],
  },
  {
    key: "clock_methods",
    label: "Check-in method",
    kind: "methods",
    synonyms: ["check-in method", "attendance method", "punch method", "clock method", "طريقة الحضور", "طريقة البصمة"],
  },
];
export const FIELD_BY = new Map(IMPORT_FIELDS.map((f) => [f.key, f]));

/* ---- reading the sheet -------------------------------------------------------- */

export type Cell = string | number | boolean | Date | null;

/** CSV, quoted fields and all; the delimiter is whichever of , ; tab the first line uses most. */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, "");
  const first = src.split(/\r?\n/, 1)[0] ?? "";
  const counts = [",", ";", "\t"].map((d) => [d, first.split(d).length] as const);
  const delim = counts.sort((a, b) => b[1] - a[1])[0][0];
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === delim) {
      row.push(cell);
      cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += c;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[_.:#*()\[\]]/g, " ")
    .replace(/[أإآ]/g, "ا")
    .replace(/\s+/g, " ")
    .trim();

/** The field a heading names, or null. */
export function fieldOfHeading(h: string): string | null {
  const n = norm(h);
  if (!n) return null;
  for (const f of IMPORT_FIELDS) if (norm(f.label) === n || f.synonyms.some((s) => norm(s) === n)) return f.key;
  for (const f of IMPORT_FIELDS) if (f.synonyms.some((s) => norm(s).length > 3 && n.includes(norm(s)))) return f.key;
  return null;
}

/** The heading row: the first of the top ten rows whose cells name three or more fields. */
export function findHeader(rows: Cell[][]): number {
  for (let i = 0; i < Math.min(10, rows.length); i++) {
    const hits = rows[i].filter((c) => typeof c === "string" && fieldOfHeading(c)).length;
    if (hits >= 3) return i;
  }
  return 0;
}

export function autoMap(headers: Cell[]): (string | null)[] {
  const used = new Set<string>();
  return headers.map((h) => {
    const k = typeof h === "string" ? fieldOfHeading(h) : null;
    if (!k || used.has(k)) return null;
    used.add(k);
    return k;
  });
}

/* ---- turning a cell into a value ------------------------------------------------ */

const AR_DIGITS = "٠١٢٣٤٥٦٧٨٩";
const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
export const westernDigits = (s: string) =>
  s.replace(/[٠-٩]/g, (d) => String(AR_DIGITS.indexOf(d))).replace(/[۰-۹]/g, (d) => String(FA_DIGITS.indexOf(d)));

const iso = (y: number, m: number, d: number) => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt.toISOString().slice(0, 10);
};

/**
 * Hijri to Gregorian by the Umm al-Qura calendar, the Kingdom's official one:
 * a first guess from the tabular calendar, then the day whose Umm al-Qura
 * date matches exactly. Where the browser has no Umm al-Qura calendar, the
 * tabular date is used (it can be a day or two out) and the caller warns.
 */
export function hijriToIso(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1 || d > 30) return null;
  const jd = Math.floor((11 * y + 3) / 30) + 354 * y + 30 * m - Math.floor((m - 1) / 2) + d + 1948440 - 385;
  const guess = (jd - 2440587.5) * 86_400_000 + 43_200_000;
  try {
    const fmt = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura-nu-latn", {
      timeZone: "UTC",
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
    for (let off = -5; off <= 5; off++) {
      const t = guess + off * 86_400_000;
      const parts = Object.fromEntries(fmt.formatToParts(new Date(t)).map((p) => [p.type, p.value]));
      if (Number(parts.year) === y && Number(parts.month) === m && Number(parts.day) === d)
        return new Date(t).toISOString().slice(0, 10);
    }
  } catch {
    /* no Umm al-Qura calendar here: the tabular date stands */
  }
  return new Date(guess).toISOString().slice(0, 10);
}

export interface Parsed {
  value: unknown;
  warning?: string;
  error?: string;
}

export function parseDate(c: Cell): Parsed {
  if (c instanceof Date) return { value: c.toISOString().slice(0, 10) };
  if (typeof c === "number") {
    if (c > 20000 && c < 80000)
      return { value: new Date(Date.UTC(1899, 11, 30) + c * 86_400_000).toISOString().slice(0, 10) };
    return { value: null, error: "not a date" };
  }
  const s = westernDigits(String(c ?? "").trim())
    .replace(/\s*هـ?$/, "")
    .replace(/\s*(AH|H)$/i, "");
  if (!s) return { value: null };
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  let y: number, mo: number, d: number;
  if (m) [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  else if ((m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/)))
    [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
  else {
    const t = Date.parse(s);
    if (!Number.isNaN(t) && /[a-z]/i.test(s))
      return { value: new Date(t - new Date(t).getTimezoneOffset() * 60000).toISOString().slice(0, 10) };
    return { value: null, error: `"${s}" is not a date` };
  }
  if (y < 1600) {
    const g = hijriToIso(y, mo, d);
    return g ? { value: g, warning: `Hijri ${s} read as ${g}` } : { value: null, error: `"${s}" is not a Hijri date` };
  }
  const v = iso(y, mo, d);
  if (!v && m && mo > 12 && d <= 12) {
    const swapped = iso(y, d, mo);
    if (swapped) return { value: swapped, warning: `${s} read as month/day` };
  }
  return v ? { value: v } : { value: null, error: `"${s}" is not a date` };
}

export function parseNumber(c: Cell): Parsed {
  if (typeof c === "number") return { value: c };
  const s = westernDigits(String(c ?? ""))
    .replace(/SAR|SR|ر\.?س\.?|ريال/gi, "")
    .replace(/[,\s٬]/g, "")
    .replace("٫", ".");
  if (!s) return { value: null };
  const n = Number(s);
  return Number.isFinite(n) ? { value: n } : { value: null, error: `"${c}" is not a number` };
}

const NAT: Record<string, string> = {
  saudi: "SA",
  "saudi arabia": "SA",
  ksa: "SA",
  سعودي: "SA",
  سعودية: "SA",
  السعودية: "SA",
  egypt: "EG",
  egyptian: "EG",
  مصري: "EG",
  مصر: "EG",
  india: "IN",
  indian: "IN",
  هندي: "IN",
  الهند: "IN",
  pakistan: "PK",
  pakistani: "PK",
  باكستاني: "PK",
  bangladesh: "BD",
  bangladeshi: "BD",
  بنغالي: "BD",
  بنجلاديشي: "BD",
  philippines: "PH",
  filipino: "PH",
  philippine: "PH",
  فلبيني: "PH",
  jordan: "JO",
  jordanian: "JO",
  أردني: "JO",
  اردني: "JO",
  sudan: "SD",
  sudanese: "SD",
  سوداني: "SD",
  yemen: "YE",
  yemeni: "YE",
  يمني: "YE",
  nepal: "NP",
  nepali: "NP",
  nepalese: "NP",
  نيبالي: "NP",
  "sri lanka": "LK",
  "sri lankan": "LK",
  سريلانكي: "LK",
  syria: "SY",
  syrian: "SY",
  سوري: "SY",
  lebanon: "LB",
  lebanese: "LB",
  لبناني: "LB",
  "united kingdom": "GB",
  british: "GB",
  uk: "GB",
  "united states": "US",
  american: "US",
  usa: "US",
  indonesia: "ID",
  indonesian: "ID",
  إندونيسي: "ID",
  ethiopia: "ET",
  ethiopian: "ET",
  kenya: "KE",
  kenyan: "KE",
  tunisia: "TN",
  tunisian: "TN",
  morocco: "MA",
  moroccan: "MA",
  palestine: "PS",
  palestinian: "PS",
  فلسطيني: "PS",
};

function word(s: Cell) {
  return norm(String(s ?? ""));
}

function pick(c: Cell, table: [RegExp, string][], what: string): Parsed {
  const s = word(c);
  if (!s) return { value: null };
  for (const [re, v] of table) if (re.test(s)) return { value: v };
  return { value: null, error: `"${c}" is not a ${what} this import knows` };
}

export function parseCell(kind: FieldKind, c: Cell): Parsed {
  switch (kind) {
    case "date":
      return parseDate(c);
    case "number":
      return parseNumber(c);
    case "nationality": {
      const s = word(c);
      if (!s) return { value: null };
      if (/^[a-z]{2}$/i.test(s)) return { value: s.toUpperCase() };
      const v = NAT[s] ?? NAT[s.replace(/^ال/, "")];
      return v
        ? { value: v }
        : { value: null, error: `"${c}" — write the nationality as a two-letter code, e.g. SA, IN` };
    }
    case "employment":
      return pick(
        c,
        [
          [/out|contract|agency|مقاول|اسناد|إسناد|توريد/, "outsourced"],
          [/direct|perm|staff|own|مباشر|رسمي|موظف/, "direct"],
        ],
        "kind of employment",
      );
    case "contract":
      return pick(
        c,
        [
          [/unlimit|indefinite|open|permanent|غير محدد/, "indefinite"],
          [/limit|fixed|term|temporary|محدد/, "fixed"],
        ],
        "contract type",
      );
    case "status":
      return pick(
        c,
        [
          [/leave|vacation|اجاز|إجاز/, "on_leave"],
          [/left|termin|resign|exit|inactive|ended|مستقيل|منته|انتهت|ترك/, "left"],
          [/active|working|current|on duty|على رأس|نشط|فعال/, "active"],
        ],
        "status",
      );
    case "gender":
      return pick(
        c,
        [
          [/^(f|female|woman|أنثى|انثى|ا)$/, "female"],
          [/^(m|male|man|ذكر|ذ)$/, "male"],
        ],
        "gender",
      );
    case "marital":
      return pick(
        c,
        [
          [/single|أعزب|اعزب|عزباء/, "single"],
          [/married|متزوج/, "married"],
          [/divorc|مطلق/, "divorced"],
          [/widow|أرمل|ارمل/, "widowed"],
        ],
        "marital status",
      );
    case "trade": {
      const s = word(c);
      if (!s) return { value: null };
      const hit = (TRADES as readonly string[]).find((t) => s.includes(t));
      if (hit) return { value: hit };
      const extra: [RegExp, string][] = [
        [/electric|كهرب/, "electrical"],
        [/civil|مدني/, "civil"],
        [/mechan|hvac|ميكانيك|تكييف/, "mechanical"],
        [/ict|it |system|اتصالات|تقنية/, "systems"],
        [/fire|safety|سلامة|حريق/, "safety"],
        [/admin|إدار|ادار/, "administration"],
        [/operat|عمليات|تشغيل/, "operations"],
      ];
      for (const [re, v] of extra) if (re.test(s)) return { value: v };
      return { value: null, warning: `trade "${c}" not recognised; left empty` };
    }
    case "phone": {
      const s = westernDigits(String(c ?? "")).replace(/[^\d+]/g, "");
      if (!s) return { value: null };
      if (/^05\d{8}$/.test(s)) return { value: `+966${s.slice(1)}` };
      if (/^5\d{8}$/.test(s)) return { value: `+966${s}` };
      if (/^(00)?9665\d{8}$/.test(s)) return { value: `+${s.replace(/^00/, "")}` };
      if (/^\+\d{8,15}$/.test(s)) return { value: s };
      return { value: s, warning: `mobile "${c}" is not in a form SMS can use` };
    }
    case "email": {
      const s = String(c ?? "").trim();
      if (!s) return { value: null };
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s)
        ? { value: s }
        : { value: null, warning: `"${s}" is not an email address; left empty` };
    }
    case "methods": {
      const s = word(c);
      if (!s) return { value: null };
      const out = new Set<string>();
      if (/finger|بصم/.test(s)) out.add("terminal_fingerprint");
      if (/face|وجه/.test(s)) out.add("terminal_face");
      if (/phone|mobile|gps|location|app|جوال|موقع/.test(s)) out.add("phone_location");
      return out.size
        ? { value: [...out] }
        : { value: null, warning: `check-in method "${c}" not recognised; HR sets it on the file` };
    }
    default: {
      const s = typeof c === "number" ? String(c) : String(c ?? "").trim();
      return { value: s || null };
    }
  }
}

/* ---- a row, ready to send ------------------------------------------------------- */

export interface PreparedRow {
  row: number;
  data: Record<string, unknown>;
  loginEmail: string | null;
  leaveBalance: number | null;
  errors: string[];
  warnings: string[];
  name: string;
  employeeNo: string;
}

export function prepareRows(
  rows: Cell[][],
  header: number,
  mapping: (string | null)[],
  opts: { canPay: boolean },
): PreparedRow[] {
  const out: PreparedRow[] = [];
  const seen = new Map<string, number>();
  for (let i = header + 1; i < rows.length; i++) {
    const cells = rows[i];
    if (!cells || cells.every((c) => c == null || String(c).trim() === "")) continue;
    const data: Record<string, unknown> = {};
    const errors: string[] = [];
    const warnings: string[] = [];
    let loginEmail: string | null = null;
    let leaveBalance: number | null = null;
    const ec: Record<string, string> = {};
    mapping.forEach((key, col) => {
      if (!key) return;
      const f = FIELD_BY.get(key);
      if (!f) return;
      if (f.pay && !opts.canPay) return;
      const p = parseCell(f.kind, cells[col] ?? null);
      if (p.error) (f.required ? errors : warnings).push(`${f.label}: ${p.error}`);
      if (p.warning) warnings.push(`${f.label}: ${p.warning}`);
      if (p.value == null) return;
      if (key === "login_email") loginEmail = String(p.value);
      else if (key === "leave_balance") leaveBalance = Number(p.value);
      else if (key === "ec_name") ec.name = String(p.value);
      else if (key === "ec_phone") ec.phone = String(p.value);
      else data[key] = p.value;
    });
    if (ec.name || ec.phone) data.emergency_contact = ec;
    /* the employee number is always needed; the rest only for somebody new —
       a row updating a person already on the register may carry just a phone */
    for (const f of IMPORT_FIELDS.filter((x) => x.required))
      if (data[f.key] == null && !errors.some((e) => e.startsWith(f.label)))
        (f.key === "employee_no" ? errors : warnings).push(
          f.key === "employee_no" ? `${f.label}: missing` : `${f.label}: missing (needed for someone new)`,
        );
    if (data.status === "left" && !data.left_on) {
      warnings.push("Status is left but no leaving date; imported as active");
      delete data.status;
    }
    const no = String(data.employee_no ?? "");
    if (no) {
      if (seen.has(no)) errors.push(`Employee number ${no} is also on row ${seen.get(no)}`);
      else seen.set(no, i + 1);
    }
    out.push({
      row: i + 1,
      data,
      loginEmail,
      leaveBalance,
      errors,
      warnings,
      name: String(data.name ?? ""),
      employeeNo: no,
    });
  }
  return out;
}

/** A template with every column the import reads, and one example row. */
export function templateCsv(): string {
  const cols = IMPORT_FIELDS;
  const example: Record<string, string> = {
    employee_no: "E1001",
    name: "Abdullah Al-Harbi",
    nationality: "SA",
    hired_on: "2019-03-01",
    department: "Airfield Maintenance",
    job_title: "AGL technician",
    trade: "electrical",
    grade: "T2",
    employment: "direct",
    contract_type: "indefinite",
    status: "active",
    basic: "7500",
    housing: "1875",
    transport: "750",
    date_of_birth: "1990-05-04",
    gender: "male",
    marital_status: "married",
    id_number: "1012345678",
    id_expiry: "2029-01-15",
    phone: "0551234567",
    email: "a.alharbi@example.com",
    leave_balance: "18",
    clock_methods: "fingerprint",
  };
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return `﻿${cols.map((c) => esc(c.label)).join(",")}\r\n${cols.map((c) => esc(example[c.key] ?? "")).join(",")}\r\n`;
}

export interface ImportResult {
  row: number;
  employeeNo: string | null;
  action: "added" | "updated" | "unchanged" | "skipped" | "failed";
  error?: string;
  warning?: string;
  changed?: string[];
  id?: string;
}

export interface ImportResponse {
  dryRun: boolean;
  counts: Record<"added" | "updated" | "unchanged" | "skipped" | "failed", number>;
  results: ImportResult[];
}
